import { Pane, Radio } from 'components/common';

import { useEffect, useMemo, useState } from 'react';
import AllExportsTable from './Tables/AllExportsTable';
import {
  resetSignalRExportRefresh,
  setFilters,
  setView,
} from 'actions/exportHistory';
import {
  getExportToRefresh,
  getFilters,
  getView,
} from 'selectors/exportHistory';
import { useAuth, useGet } from 'hooks';
import {
  allExportHistory,
  getUserCredits,
  refreshExportHistory,
} from 'services/api';
import StatsProgress from 'components/Export/Summary/StatsProgress';
import { ActionsHeader, StatsContainer } from './styled';
import { isAcademicFull, isIndustryFull } from 'utils/helpers/license';
import ExportHistoryFilters from './Filters';
import { useDispatch, useSelector } from 'react-redux';
import { ExportTemplateListTab } from 'components/ExportTemplates/ExportTemplateListTab';

const fullRefreshOrderFields = [
  'academicCredits',
  'biopharmaCredits',
  'status',
  'numberOfEmails',
];

const ExportHistory = () => {
  const dispatch = useDispatch();

  const { user } = useAuth();
  const showUserCreditInformation =
    user.isOnCreditSystem &&
    ((isAcademicFull(user) && user.showAcademicCredits) ||
      (isIndustryFull(user) && user.showIndustryCredits));

  const filters = useSelector(getFilters(user.userId));
  const currentView = useSelector(getView(user.userId));

  const [creditData, setCreditData] = useState({
    academicCreditsTotal: 0,
    academicCreditsUsed: 0,
    industryCreditsTotal: 0,
    industryCreditsUsed: 0,
  });

  const [localData, setLocalData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [historyToUpdate, setHistoryToUpdate] = useState();

  const [{ res: allData, loading: isAllLoading }, getAllExportData] = useGet({
    url: allExportHistory,
    onMount: false,
  });

  useEffect(() => {
    if (user.isUserManager && !filters?.userIds?.length) {
      dispatch(setFilters(user.userId, { ...filters, userIds: [user.userId] }));
    }
    setIsInitialLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAllLoading) {
      setLocalData(allData);
    }
  }, [allData, isAllLoading]);

  const [
    { res: creditsRes, loading: creditsLoading },
    getUserCreditData,
  ] = useGet({
    url: getUserCredits,
    onMount: false,
  });

  const { id: updatedExportHistoryId } = useSelector(getExportToRefresh) || {};

  const [
    { res: refreshedHistory, loading: isLoadingRefresh },
    refreshExportHistoryItem,
  ] = useGet({
    url: refreshExportHistory(updatedExportHistoryId),
    onMount: false,
  });

  useEffect(() => {
    if (updatedExportHistoryId) {
      const history = localData?.data?.find(
        x => x.id === updatedExportHistoryId
      );
      if (history) {
        if (fullRefreshOrderFields.includes(filters.orderBy)) {
          dispatch(setFilters(user.userId, { ...filters }));
        }

        refreshExportHistoryItem();
        setHistoryToUpdate(history);
        if (showUserCreditInformation) {
          getUserCreditData();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updatedExportHistoryId,
    localData,
    refreshExportHistoryItem,
    filters.orderBy,
    getUserCreditData,
    showUserCreditInformation,
    dispatch,
    user.userId,
  ]);

  useEffect(() => {
    if (refreshedHistory && historyToUpdate && !isLoadingRefresh) {
      const replaceIndex = localData?.data?.findIndex(
        x => x === historyToUpdate
      );

      if (replaceIndex > -1) {
        localData.data[replaceIndex] = refreshedHistory;
        setLocalData({ ...localData, data: [...localData.data] });
      }
      dispatch(resetSignalRExportRefresh());
      setHistoryToUpdate(null);
    }
  }, [
    refreshedHistory,
    historyToUpdate,
    localData,
    isLoadingRefresh,
    dispatch,
  ]);

  function updateFilters(filters) {
    dispatch(setFilters(user.userId, filters));
  }

  useEffect(() => {
    if (isInitialLoad || currentView !== 'activity') return;

    if (showUserCreditInformation) {
      getUserCreditData();
    }

    let searchFilters = { ...filters };
    if (!user.isUserManager) {
      searchFilters.userIds = [];
    }

    getAllExportData({ query: searchFilters });
  }, [
    showUserCreditInformation,
    getUserCreditData,
    getAllExportData,
    filters,
    user.isUserManager,
    isInitialLoad,
    currentView,
  ]);

  useEffect(() => {
    if (!creditsLoading) {
      setCreditData(creditsRes);
    }
  }, [creditsRes, creditsLoading]);

  const CreditsBar = ({ creditData }) => {
    return (
      <>
        <StatsContainer>
          <StatsProgress
            blockTimeout={true}
            showAcademicCredits={
              user.showAcademicCredits && isAcademicFull(user)
            }
            academicCreditsUsed={creditData?.academicCreditsUsed ?? 0}
            academicCreditsRequired={0}
            academicCreditsTotal={creditData?.academicCreditsTotal ?? 0}
            showIndustryCredits={
              user.showIndustryCredits && isIndustryFull(user)
            }
            industryCreditsUsed={creditData?.industryCreditsUsed ?? 0}
            industryCreditsRequired={0}
            industryCreditsTotal={creditData?.industryCreditsTotal ?? 0}
            renewDays={creditData?.daysUntilReset}
          />
        </StatsContainer>
      </>
    );
  };

  const handleChangeView = view => {
    dispatch(setView(user.userId, view));
  };

  const Title = () => {
    const viewOptions = useMemo(
      () => [
        { label: 'Export Activity', value: 'activity' },
        { label: 'Export Templates', value: 'template' },
      ],
      []
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ marginRight: 15 }}>Export Management</span>
        <Radio
          data-testid='export-management-tabs'
          value={currentView}
          options={viewOptions}
          optionType='button'
          onChange={handleChangeView}
          style={{ fontWeight: 'normal' }}
          size='middle'
        />
      </div>
    );
  };

  return (
    <Pane title={<Title />}>
      {currentView === 'activity' ? (
        <>
          <ActionsHeader>
            <ExportHistoryFilters
              fullWidth={!showUserCreditInformation}
              filters={filters}
              user={user}
              updateFilters={updateFilters}
            />
            {showUserCreditInformation && (
              <CreditsBar creditData={creditData} />
            )}
          </ActionsHeader>
          <AllExportsTable
            user={user}
            isLoading={isAllLoading}
            data={localData}
            updateFilters={updateFilters}
            filters={filters}
          />
        </>
      ) : (
        <ExportTemplateListTab />
      )}
    </Pane>
  );
};

export default ExportHistory;
